import React from 'react'
import styles from 'styles/components/ReportSummary.module.sass'
import ReportDetailsCard from 'components/cards/ReportDetailsCard';
import ReportDetails from 'interface/ReportDetails';
import ZeroneReport from 'interface/ZeroneReport';
import ReusableIcon from './ReusableIcon';
import { useNavigate } from 'react-router-dom';

/**
 * Props for the ReportSummary component.
 *
 * @interface ReportSummaryProps
 * @property {ReportContent} report - The report data to be displayed in the summary.
 */
interface ReportSummaryProps {
    report: ZeroneReport;
    ableToPurchase: boolean;
    hasAccessToReport: boolean;
    handleBuyReportClick: () => void;
}

// Map of category names to their corresponding IDs for navigation purposes.
const categoryMap: Record<string, string> = {
    'Trending Topics' : '9f0eeee2-d72c-40cd-a0c0-52572de54e3e',
    'Agriculture' : 'a5ba4967-62cd-4ffe-bc0f-4deb2d15c04b',
    'Travel, Tourism & Hospitality' : 'a91089cd-ae7b-49d0-a99e-4bc84450139c',
    'Retail & Consumer Goods' : '5bf3e874-32f7-4b9e-b2f5-eca1cee34133',
    'Economic Indicators' : '5d8c1e92-d309-4c0b-aa46-4f6bf9d41c7e'
}

/**
 * ReportSummary component displays a summarized view of a report.
 *
 * This component includes key details such as the report's category, title,
 * description, publication year, and author information. It also dynamically 
 * applies background images based on the report's metadata.
 *
 * @component
 *
 * @example
 * import ReportSummary from 'components/common/ReportSummary';
 * 
 * const report = {
 *   id: "123",
 *   dataset: "Market Analysis",
 *   year: "2024",
 *   title: "The State of African Markets",
 *   description: "An in-depth look at market trends in Africa.",
 *   author: {
 *     name: "Jane Doe",
 *     imageUrl: "https://example.com/jane.jpg"
 *   },
 *   meta: {
 *     cover: "https://example.com/cover.jpg"
 *   }
 * };
 *
 * <ReportSummary report={report} />
 *
 * @param {ReportSummaryProps} props - The props for the component.
 * @returns {JSX.Element | null} The rendered ReportSummary component or null if no report data is provided.
 */
const ReportSummary: React.FC<ReportSummaryProps> = ({ report, ableToPurchase, hasAccessToReport, handleBuyReportClick }) => {
    const navigate = useNavigate();
    if (!report) return null;

    const reportDetails: ReportDetails = {
        id: report.id,
        title: report.title,
        type: report.type,
        meta: report.meta,
        formats: report.links,
    }

    const handleBuyClicked = () => {
        handleBuyReportClick();
    }

    const handleCrumbClick = (crumb: string) => {
        navigate(`/${crumb}`);
    }

    return (
        <div
            key={report.id} 
            className={styles['report-summary']} 
            style={{
                background: `linear-gradient(rgba(233, 247, 255, 0.9), rgba(219, 242, 255, 0.9)), url("${report.meta.cover || ''}") no-repeat center center / cover`, 
            }}
        >   
            <div className={styles['main-wrapper']}>
                <div className={styles['left-section']}>
                    <div className={styles['crumbs-wrapper']}>
                        <span className={`${styles['crumb']} ${styles['active']}`} onClick={() => handleCrumbClick('report/categories')}>Reports</span>
                        <ReusableIcon icon={'tabler:chevron-right'} className={styles['crumb-icon'] || 'crumb-icon'} color="#111111" />
                        <span className={`${styles['crumb']} ${styles['active']}`} onClick={() => handleCrumbClick(`report/category/${categoryMap[report.category]}`)}>{report.category}</span>
                        <ReusableIcon icon={'tabler:chevron-right'} className={styles['crumb-icon'] || 'crumb-icon'} color="#111111" />
                        <span className={styles['crumb']}>{report.title}</span>
                    </div>
                    <div className={styles['top']}>
                        <h3 className={styles['title']}>{report.title}</h3>
                        <span className={styles['category']}>{report.dataset}</span>
                        <p className={styles['date-wrapper']}> • <span className={styles['date']}>{report.year}</span></p>
                    </div>
                    {report.description
                        ?.replace(/\\n/g, '\n') // Convert escaped newlines to real newlines
                        .split(/\n+/)
                        .map((paragraph, index) => (
                            <p key={index} className={styles['catchy-phrase']}>
                                {paragraph.trim()}
                            </p>
                        ))
                    }
                    {/* eslint-disable @typescript-eslint/no-unsafe-member-access */}
                    {/* {report.author && (
                        <div className={styles['author']}>
                            <img src={report.author.imageUrl} alt={report.author.name} className={styles['author-image']} />
                            <p className={styles['name-role']}>by <span className={styles['author-name']}>{report.author.name}</span></p>
                        </div>
                    )} */}
                    {/* eslint-enable @typescript-eslint/no-unsafe-member-access */}
                </div>
                {/* <div className={styles['right-section']} style={{ backgroundImage: `url(${report.meta.cover || ''})` }}></div> */}
                <div className={styles['right-section']}>
                    <ReportDetailsCard 
                        details={reportDetails}
                        ableToPurchase={ableToPurchase}
                        hasAccessToReport={hasAccessToReport}
                        handleBuyReportClick={handleBuyClicked}
                    />
                </div>
            </div>
        </div>
    )
}

export default ReportSummary;